// Loading, empty, error and status feedback: Skeleton, EmptyState, ErrorState, Banner, ProgressBar.
import { useState, type ReactNode } from 'react'
import { CircleAlert, CircleCheck, Info, RotateCw, TriangleAlert, WifiOff, X, ShieldAlert, SearchX } from 'lucide-react'
import { cn } from './cn'
import { Button } from './button'
import { ApiError } from '@/lib/api'

// ─── Skeleton ───────────────────────────────────────────────────────────────────────────

/** Placeholder block shaped like the content that is loading (no spinners on page loads). */
export function Skeleton({ className, rounded = 'md', style }: { className?: string; rounded?: 'sm' | 'md' | 'full' | 'card'; style?: React.CSSProperties }) {
  return (
    <div aria-hidden style={style} className={cn(
      'animate-shimmer bg-surface-active',
      rounded === 'full' ? 'rounded-full' : rounded === 'card' ? 'rounded-card' : rounded === 'sm' ? 'rounded-[4px]' : 'rounded-md',
      className,
    )} />
  )
}

/** Paragraph placeholder: `<SkeletonText lines={3} />`. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  const widths = ['w-full', 'w-11/12', 'w-4/5', 'w-2/3', 'w-3/4']
  return (
    <div className={cn('flex flex-col gap-2', className)} aria-hidden>
      {Array.from({ length: lines }, (_, i) => <Skeleton key={i} className={cn('h-3', i === lines - 1 ? 'w-3/5' : widths[i % widths.length])} />)}
    </div>
  )
}

/** A card-shaped page placeholder: header line + text lines. */
export function SkeletonCard({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-4 rounded-card border border-border bg-surface p-5 shadow-card', className)} role="status" aria-label="Loading">
      <Skeleton className="h-4 w-1/3" />
      <SkeletonText lines={lines} />
    </div>
  )
}

// ─── EmptyState ─────────────────────────────────────────────────────────────────────────

export interface EmptyStateProps {
  /** Illustration slot (lucide icon, SVG, image). Icons get a soft circular backdrop. */
  illustration?: ReactNode
  title: ReactNode
  /** Teach: what this is and why it's useful. */
  description?: ReactNode
  /** One clear primary action. */
  action?: ReactNode
  secondaryAction?: ReactNode
  /** Smaller variant for inside cards/tables/popovers. */
  compact?: boolean
  className?: string
}

/**
 * Empty state that teaches. `<EmptyState illustration={<Tag/>} title="Add your first product"
 * description="Products are what you sell…" action={<Button variant="primary">Add product</Button>} />`
 */
export function EmptyState({ illustration, title, description, action, secondaryAction, compact, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center text-center', compact ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14', className)}>
      {illustration && (
        <div className={cn(
          'mb-1 grid place-items-center text-text-subtle',
          compact ? 'size-10 rounded-full bg-surface-sunken [&>svg]:size-5' : 'size-14 rounded-2xl border border-border bg-gradient-to-b from-surface to-surface-sunken shadow-card [&>svg]:size-6 [&>svg]:text-text-muted',
        )}>{illustration}</div>
      )}
      <div className="flex max-w-sm flex-col gap-1">
        <h3 className={cn('font-semibold text-text', compact ? 'text-base' : 'text-md')}>{title}</h3>
        {description && <p className="text-base text-text-muted">{description}</p>}
      </div>
      {(action || secondaryAction) && <div className="mt-2 flex flex-wrap items-center justify-center gap-2">{action}{secondaryAction}</div>}
    </div>
  )
}

/** Empty result for a search/filter (different from "nothing created yet"). */
export function NoResults({ query, onClear, className }: { query?: string; onClear?: () => void; className?: string }) {
  return (
    <EmptyState compact className={className} illustration={<SearchX />}
      title={query ? <>No results for “{query}”</> : 'No matching results'}
      description="Try a different search or remove some filters."
      action={onClear && <Button size="sm" onClick={onClear}>Clear filters</Button>} />
  )
}

// ─── ErrorState ─────────────────────────────────────────────────────────────────────────

/** Turn anything thrown into a merchant-safe message. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof TypeError && /fetch|network/i.test(error.message)) return 'Can’t reach the server. Check your connection and try again.'
  if (error instanceof Error && error.message) return error.message
  return 'Something went wrong.'
}

/**
 * Inline error with retry: `if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />`.
 * Knows ApiError statuses (403 → no permission, 404 → not found, offline).
 */
export function ErrorState({ error, title, onRetry, retrying, compact, className }: { error?: unknown; title?: ReactNode; onRetry?: () => void; retrying?: boolean; compact?: boolean; className?: string }) {
  const status = error instanceof ApiError ? error.status : 0
  const offline = typeof navigator !== 'undefined' && !navigator.onLine
  const icon = offline ? <WifiOff /> : status === 403 ? <ShieldAlert /> : <CircleAlert />
  const heading = title ?? (offline ? 'You’re offline' : status === 403 ? 'You don’t have access to this' : status === 404 ? 'Not found' : 'Couldn’t load this')
  const text = status === 403 ? 'Ask the store owner to give you permission.' : status === 404 ? 'It may have been deleted, or the link is wrong.' : errorMessage(error)
  return (
    <div role="alert" className={cn('flex flex-col items-center text-center', compact ? 'gap-2 px-4 py-6' : 'gap-3 px-6 py-12', className)}>
      <div className={cn('grid place-items-center rounded-full bg-danger-soft text-danger-strong', compact ? 'size-9 [&>svg]:size-4' : 'size-11 [&>svg]:size-5')}>{icon}</div>
      <div className="flex max-w-sm flex-col gap-1">
        <h3 className="text-base font-semibold text-text">{heading}</h3>
        <p className="text-base text-text-muted">{text}</p>
      </div>
      {onRetry && status !== 403 && status !== 404 && (
        <Button size="sm" icon={<RotateCw />} loading={retrying} onClick={onRetry} className="mt-1">Try again</Button>
      )}
    </div>
  )
}

// ─── Banner ─────────────────────────────────────────────────────────────────────────────

export type BannerTone = 'info' | 'warning' | 'critical' | 'success' | 'neutral'
const bannerTones: Record<BannerTone, { box: string; icon: ReactNode; iconCls: string }> = {
  info: { box: 'border-info/25 bg-info-soft', icon: <Info />, iconCls: 'text-info-strong' },
  warning: { box: 'border-warning/30 bg-warning-soft', icon: <TriangleAlert />, iconCls: 'text-warning-strong' },
  critical: { box: 'border-danger/25 bg-danger-soft', icon: <CircleAlert />, iconCls: 'text-danger-strong' },
  success: { box: 'border-success/25 bg-success-soft', icon: <CircleCheck />, iconCls: 'text-success-strong' },
  neutral: { box: 'border-border bg-surface-sunken', icon: <Info />, iconCls: 'text-text-muted' },
}

export interface BannerProps {
  tone?: BannerTone
  title?: ReactNode
  children?: ReactNode
  /** Buttons/links on the right (wrap below on mobile). */
  actions?: ReactNode
  /** Show × and call this when dismissed. */
  onDismiss?: () => void
  /** Remember dismissal in localStorage under this key (implies dismissible). */
  dismissKey?: string
  icon?: ReactNode
  className?: string
  /** Full-bleed strip for the shell (no rounded border). */
  strip?: boolean
}

/**
 * Contextual message: `<Banner tone="warning" title="Payments are in test mode" actions={<Button size="sm">Go live</Button>}>…</Banner>`.
 */
export function Banner({ tone = 'info', title, children, actions, onDismiss, dismissKey, icon, className, strip }: BannerProps) {
  const [hidden, setHidden] = useState(() => {
    if (!dismissKey) return false
    try { return localStorage.getItem(`banner:${dismissKey}`) === '1' } catch { return false }
  })
  if (hidden) return null
  const t = bannerTones[tone]
  const dismiss = () => {
    if (dismissKey) { try { localStorage.setItem(`banner:${dismissKey}`, '1') } catch { /* ignore */ } }
    setHidden(true)
    onDismiss?.()
  }
  return (
    <div role={tone === 'critical' || tone === 'warning' ? 'alert' : 'status'}
      className={cn('flex gap-3 border text-base text-text', strip ? 'border-x-0 border-t-0 px-4 py-3 sm:px-6' : 'rounded-card px-4 py-3', t.box, className)}>
      <span className={cn('flex h-5 shrink-0 items-center [&>svg]:size-4', t.iconCls)}>{icon ?? t.icon}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          {title && <p className="font-semibold">{title}</p>}
          {children && <div className="text-text-muted [&_a]:font-medium [&_a]:text-text [&_a]:underline [&_a]:underline-offset-2">{children}</div>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {(onDismiss || dismissKey) && (
        <span className="flex h-5 shrink-0 items-center"><button type="button" onClick={dismiss} aria-label="Dismiss"
          className="-mr-1 grid size-6 shrink-0 place-items-center rounded-md text-text-muted hover:bg-black/5 hover:text-text dark:hover:bg-white/10">
          <X className="size-4" />
        </button></span>
      )}
    </div>
  )
}

// ─── ProgressBar ────────────────────────────────────────────────────────────────────────

/**
 * `<ProgressBar value={42} />` (0–100), `indeterminate` for unknown duration, `tone` for meters
 * (usage near a plan limit → warning/danger). Optional label + value text above.
 */
export function ProgressBar({ value, max = 100, indeterminate, tone = 'primary', label, valueLabel, size = 'md', className }: {
  value?: number; max?: number; indeterminate?: boolean; tone?: 'primary' | 'success' | 'warning' | 'danger' | 'neutral'
  label?: ReactNode; valueLabel?: ReactNode; size?: 'sm' | 'md'; className?: string
}) {
  const pct = indeterminate ? null : Math.max(0, Math.min(100, ((value ?? 0) / (max || 1)) * 100))
  const fill = { primary: 'bg-primary', success: 'bg-success', warning: 'bg-warning', danger: 'bg-danger', neutral: 'bg-text-subtle' }[tone]
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {(label || valueLabel) && (
        <div className="flex items-baseline justify-between gap-2 text-sm">
          <span className="text-text-muted">{label}</span>
          <span className="font-medium text-text tabular">{valueLabel ?? (pct !== null ? `${Math.round(pct)}%` : '')}</span>
        </div>
      )}
      <div role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={indeterminate ? undefined : value} aria-label={typeof label === 'string' ? label : undefined}
        className={cn('relative w-full overflow-hidden rounded-full bg-surface-active', size === 'sm' ? 'h-1' : 'h-1.5')}>
        {pct !== null
          ? <div className={cn('h-full rounded-full transition-[width] duration-180 ease-out', fill)} style={{ width: `${pct}%` }} />
          : <div className={cn('absolute inset-y-0 w-2/5 rounded-full animate-indeterminate', fill)} />}
      </div>
    </div>
  )
}
